import { useCallback, useEffect, useState } from "react";

const synchronizer = new EventTarget();
// the "storage" event is emitted on changes in other contexts (tabs/documents), we'll forward it into our local events
window.addEventListener("storage", (event) => {
  synchronizer.dispatchEvent(event);
});
// one might think that it should work to use the "storage" event for local (same context/document/tab) as well, however
// it is only emitted for changes in other contexts

function useStorage(key: string, storage: Storage): [string | null, (value: string | null) => void] {
  const [value, setValue] = useState<string | null>(storage.getItem(key));

  const set = useCallback(
    (v: string | null) => {
      setValue(v);
      if (v === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, v);
      }
      // notify other hooks for the same key
      synchronizer.dispatchEvent(new StorageEvent("storage", { key, storageArea: storage, newValue: v }));
    },
    [key, storage]
  );

  // get updated on changes for the same key and storage space in other hooks and tabs
  useEffect(() => {
    if (storage === localStorage) {
      const callback = (event: StorageEvent) => {
        if (event.key === key && event.storageArea === storage) {
          setValue(event.newValue);
        }
      };
      synchronizer.addEventListener("storage", callback as any);
      return () => synchronizer.removeEventListener("storage", callback as any);
    }
  }, [storage, key]);

  return [value, set];
}

export function useLocalStorage(key: string) {
  return useStorage(key, localStorage);
}
export function useSessionStorage(key: string) {
  return useStorage(key, sessionStorage);
}
